import type { TVideoPlaybackSentryKind } from '@/helpers/reportPlayerVideoPlaybackToSentry'
import { isBenignVideoPlayInterruptError } from '@/helpers/reportPlayerVideoPlaybackToSentry'

import * as Sentry from '@sentry/vue'

/**
 * Classifies failures for sliding-window reload rules.
 * `hls_unsupported` is excluded from reload (MSE missing; reload does not help).
 */
export type TPlaybackFailureBucket
  = 'hls_fatal'
    | 'hls_unsupported'
    | 'media_decode'
    | 'media_network'
    | 'media_other'
    | 'play_failed'

const STORAGE_LAST_RELOAD_AT = 'lucimark.pb.recoveryReloadAt'
const STORAGE_RELOAD_COUNT = 'lucimark.pb.recoveryReloadCount'

/** Session web recovery reload count (0 when unavailable / private mode). */
export function getWebRecoveryReloadCount(): number {
  try {
    const count = Number(sessionStorage.getItem(STORAGE_RELOAD_COUNT) || '0')
    return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
  }
  catch {
    return 0
  }
}

/** Epoch ms of last web recovery reload (0 when none / unavailable). */
export function getWebRecoveryReloadLastAt(): number {
  try {
    const last = Number(sessionStorage.getItem(STORAGE_LAST_RELOAD_AT) || '0')
    return Number.isFinite(last) && last > 0 ? Math.floor(last) : 0
  }
  catch {
    return 0
  }
}

/** Count hls_fatal / decode failures in this window. */
const WINDOW_DECODE_HLS_MS = 10 * 60 * 1000
/** Count play_failed in this window. */
const WINDOW_PLAY_FAIL_MS = 10 * 60 * 1000
/** Low-memory–correlated failures window. */
const WINDOW_LOW_MEMORY_MS = 15 * 60 * 1000
/** Prune in-memory ring to this horizon. */
const PRUNE_MS = Math.max(WINDOW_LOW_MEMORY_MS, WINDOW_DECODE_HLS_MS, WINDOW_PLAY_FAIL_MS)

const THRESHOLD_HLS_FATAL = 3
const THRESHOLD_MEDIA_DECODE = 3
const THRESHOLD_PLAY_FAILED = 6
/** Distinct playback failures while the device reports low memory. */
const THRESHOLD_LOW_MEMORY_FAILURES = 2

const RELOAD_COOLDOWN_MS = 8 * 60 * 1000
const MAX_SESSION_RELOADS = 5
/**
 * After this much healthy wall time since the last recovery reload, clear the session reload
 * counter. sessionStorage survives `location.reload()`, so without this a burst of 5 heals
 * permanently disables web recovery until the native process dies (PLAYER-1J / heavy video
 * Slimbox/mid-tier fleets).
 */
export const RECOVERY_RELOAD_HEALTHY_RESET_MS = 45 * 60 * 1000
/**
 * Continuous {@link detectPlaybackTimerStuck} suspicion before hard-reload. Soft remount
 * paths often leave the scheduler capped; waiting for the heartbeat watchdog (5 min) is too late.
 */
export const PLAYBACK_TIMER_STUCK_RELOAD_AFTER_MS = 20_000

interface FailureEvent {
  ts: number
  bucket: TPlaybackFailureBucket
  lowMemoryMode: boolean
}

let failureEvents: FailureEvent[] = []
/** Wall ms when timer-stuck first became continuously suspected (0 when clear). */
let playbackTimerStuckSinceMs = 0
/** Latch so a stuck episode only arms one reload attempt (sessionStorage may be unavailable). */
let playbackTimerStuckReloadArmed = false

/** Test-only reset of in-memory failure history. */
export function resetPlaybackFailureReloadPolicyForTesting(): void {
  failureEvents = []
  playbackTimerStuckSinceMs = 0
  playbackTimerStuckReloadArmed = false
}

/** HTMLMediaElement error codes (same numeric values as `MediaError` in browsers). */
const MEDIA_ERR_NETWORK = 2
const MEDIA_ERR_DECODE = 3
const MEDIA_ERR_SRC_NOT_SUPPORTED = 4

export function playbackFailureBucketFromSignals(
  kind: TVideoPlaybackSentryKind,
  mediaErrorCode?: number
): TPlaybackFailureBucket {
  if (kind === 'hls_fatal') {
    return 'hls_fatal'
  }
  if (kind === 'hls_unsupported') {
    return 'hls_unsupported'
  }
  if (kind === 'play_failed') {
    return 'play_failed'
  }
  if (kind === 'media_error') {
    if (mediaErrorCode === MEDIA_ERR_DECODE || mediaErrorCode === MEDIA_ERR_SRC_NOT_SUPPORTED) {
      return 'media_decode'
    }
    if (mediaErrorCode === MEDIA_ERR_NETWORK) {
      return 'media_network'
    }
    return 'media_other'
  }
  return 'media_other'
}

/**
 * Map Media3 `PlaybackException.errorCodeName` (Kotlin `onError` `code`) onto the same
 * reload buckets as WebView `MediaError`. Decode/format storms still trip recovery reload.
 */
export function playbackFailureBucketFromHybridErrorCode(
  code: string | null | undefined
): TPlaybackFailureBucket {
  const c = (code ?? '').toUpperCase()
  if (c === '') {
    return 'media_other'
  }
  if (
    c.includes('DECOD')
    || c.includes('PARSING')
    || c.includes('FORMAT')
    || c.includes('AUDIO_TRACK')
    || c.includes('DRM')
  ) {
    return 'media_decode'
  }
  if (c.includes('IO_') || c.includes('NETWORK') || c.includes('HTTP')) {
    return 'media_network'
  }
  return 'media_other'
}

function pruneEvents(now: number) {
  const cutoff = now - PRUNE_MS
  failureEvents = failureEvents.filter(e => e.ts >= cutoff)
}

function countInWindow(
  now: number,
  windowMs: number,
  predicate: (e: FailureEvent) => boolean
): number {
  const t0 = now - windowMs
  return failureEvents.filter(e => e.ts >= t0 && predicate(e)).length
}

/**
 * Clears the session recovery-reload counter when the last reload was long enough ago.
 * Safe to call often (heartbeat / diagnostics polls).
 */
export function maybeResetRecoveryReloadCountAfterHealthyPeriod(now = Date.now()): boolean {
  try {
    const last = Number(sessionStorage.getItem(STORAGE_LAST_RELOAD_AT) || '0')
    if (!Number.isFinite(last) || last <= 0) {
      return false
    }
    if (now - last < RECOVERY_RELOAD_HEALTHY_RESET_MS) {
      return false
    }
    const count = Number(sessionStorage.getItem(STORAGE_RELOAD_COUNT) || '0')
    if (!Number.isFinite(count) || count <= 0) {
      return false
    }
    sessionStorage.setItem(STORAGE_RELOAD_COUNT, '0')
    return true
  }
  catch {
    return false
  }
}

function canAttemptReload(now: number): boolean {
  maybeResetRecoveryReloadCountAfterHealthyPeriod(now)
  try {
    const last = Number(sessionStorage.getItem(STORAGE_LAST_RELOAD_AT) || '0')
    if (Number.isFinite(last) && now - last < RELOAD_COOLDOWN_MS) {
      return false
    }
    const count = Number(sessionStorage.getItem(STORAGE_RELOAD_COUNT) || '0')
    if (Number.isFinite(count) && count >= MAX_SESSION_RELOADS) {
      return false
    }
  }
  catch {
    /* private mode / no sessionStorage */
  }
  return true
}

/**
 * Records a playback failure and returns whether the session should hard-reload the WebView
 * to recover wedged decoders / MSE (evidence-based thresholds; see docs/player-playback-recovery.md).
 *
 * Benign `play()` AbortError interrupts (pause / element removed mid-play) are **not** recorded —
 * they are expected on clip transitions and must not trip recovery reload.
 */
export function recordPlaybackFailureForReloadPolicy(input: {
  bucket: TPlaybackFailureBucket
  lowMemoryMode: boolean
  now?: number
  /** When `bucket` is `play_failed`, pass the caught error so benign interrupts are ignored. */
  caughtError?: unknown
}): { reload: boolean, reason?: string } {
  const now = input.now ?? Date.now()
  pruneEvents(now)

  if (input.bucket === 'hls_unsupported') {
    return { reload: false }
  }

  if (input.bucket === 'play_failed' && isBenignVideoPlayInterruptError(input.caughtError)) {
    return { reload: false }
  }

  failureEvents.push({
    ts: now,
    bucket: input.bucket,
    lowMemoryMode: input.lowMemoryMode
  })

  if (!canAttemptReload(now)) {
    return { reload: false }
  }

  const hlsFatal = countInWindow(now, WINDOW_DECODE_HLS_MS, e => e.bucket === 'hls_fatal')
  if (hlsFatal >= THRESHOLD_HLS_FATAL) {
    return { reload: true, reason: `policy:hls_fatal_${hlsFatal}_in_${WINDOW_DECODE_HLS_MS / 60000}m` }
  }

  const decode = countInWindow(now, WINDOW_DECODE_HLS_MS, e => e.bucket === 'media_decode')
  if (decode >= THRESHOLD_MEDIA_DECODE) {
    return { reload: true, reason: `policy:media_decode_${decode}_in_${WINDOW_DECODE_HLS_MS / 60000}m` }
  }

  const playFail = countInWindow(now, WINDOW_PLAY_FAIL_MS, e => e.bucket === 'play_failed')
  if (playFail >= THRESHOLD_PLAY_FAILED) {
    return { reload: true, reason: `policy:play_failed_${playFail}_in_${WINDOW_PLAY_FAIL_MS / 60000}m` }
  }

  const lowMemHits = countInWindow(
    now,
    WINDOW_LOW_MEMORY_MS,
    e => e.lowMemoryMode && e.bucket !== 'hls_unsupported'
  )
  if (lowMemHits >= THRESHOLD_LOW_MEMORY_FAILURES) {
    return {
      reload: true,
      reason: `policy:low_memory_playback_failures_${lowMemHits}_in_${WINDOW_LOW_MEMORY_MS / 60000}m`
    }
  }

  return { reload: false }
}

const FLUSH_MS = 1500

/**
 * Persists reload intent, emits Sentry breadcrumb + info event (searchable Issue/Discover),
 * flushes the client, then `location.reload()`.
 */
export function performPlaybackRecoveryReload(reason: string): void {
  const now = Date.now()
  let reloadCount = 1
  try {
    reloadCount = Number(sessionStorage.getItem(STORAGE_RELOAD_COUNT) || '0') + 1
    sessionStorage.setItem(STORAGE_RELOAD_COUNT, String(reloadCount))
    sessionStorage.setItem(STORAGE_LAST_RELOAD_AT, String(now))
  }
  catch {
    /* continue — still attempt reload */
  }

  try {
    Sentry.addBreadcrumb({
      category: 'player_playback_recovery',
      message: 'reload',
      level: 'info',
      data: { reason, reloadCount }
    })
    Sentry.captureMessage('Player playback recovery reload', {
      level: 'info',
      tags: {
        player_playback_recovery_reload: 'true'
      },
      fingerprint: ['player-playback-recovery-reload', reason],
      extra: { reason, reloadCount }
    })
  }
  catch {
    /* Sentry unavailable */
  }

  const reload = () => {
    window.location.reload()
  }

  try {
    void Sentry.flush(FLUSH_MS).then(reload, reload)
  }
  catch {
    reload()
  }
}

/**
 * Respects session cooldown / max reloads, then {@link performPlaybackRecoveryReload}.
 * Returns whether a reload was started.
 */
export function tryPerformPlaybackRecoveryReload(reason: string, now = Date.now()): boolean {
  if (!canAttemptReload(now)) {
    return false
  }
  performPlaybackRecoveryReload(reason)
  return true
}

/**
 * Escalate sustained playback-timer-stuck (scheduler capped / frame not leaving) to a WebView
 * recovery reload. Rising-edge episode timer; clears when suspicion drops.
 */
export function recordPlaybackTimerStuckForReloadPolicy(input: {
  suspected: boolean
  now?: number
}): { reload: boolean, reason?: string } {
  const now = input.now ?? Date.now()
  if (!input.suspected) {
    playbackTimerStuckSinceMs = 0
    playbackTimerStuckReloadArmed = false
    return { reload: false }
  }
  if (playbackTimerStuckSinceMs <= 0) {
    playbackTimerStuckSinceMs = now
    return { reload: false }
  }
  if (now - playbackTimerStuckSinceMs < PLAYBACK_TIMER_STUCK_RELOAD_AFTER_MS) {
    return { reload: false }
  }
  if (playbackTimerStuckReloadArmed) {
    return { reload: false }
  }
  if (!canAttemptReload(now)) {
    return { reload: false }
  }
  playbackTimerStuckReloadArmed = true
  return {
    reload: true,
    reason: `policy:playback_timer_stuck_${PLAYBACK_TIMER_STUCK_RELOAD_AFTER_MS}ms`
  }
}
